import React, { useCallback, useState } from "react";

import { faCheck, faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { isEqual } from "lodash";
import { Checkbox as CheckboxUI, Loader } from "semantic-ui-react";

import AssigneeFilter, { type AssigneeType } from "~/common/AssigneeFilter";
import { useID } from "~/lib/hooks";
import CycleListInput from "~/okrs/components/CycleListInput";
import { StatusNames } from "~/okrs/components/StatusInput";
import type { StreamSettingsResponseStream } from "~/stream/StreamSettings/index";
import type { TeamSettingsResponseTeam } from "~/team/TeamSettings";
import ArrowDropdown, { DropdownItem } from "~/ui/ArrowDropdown";
import Popup from "~/ui/legacy/Popup";

import type { ObjectiveStatus } from "~/__generated__/ObjectiveQueryQuery.graphql";

import stylessettings from "~/settings/css/settings.module.css";
import styles from "~/stream/StreamSettings/StreamSettings.module.css";

interface ObjectivesTabTogglerProps {
	value: boolean;
	onChange: (v: string | null) => () => void;
}

export function ObjectivesTabToggler({ value, onChange }: ObjectivesTabTogglerProps) {
	const toggleID = useID();

	const changeToggler = useCallback(
		(e: React.FormEvent<HTMLInputElement>, { checked }: { checked?: boolean }) => {
			onChange(checked ? "true" : "")();
		},
		[onChange],
	);

	return (
		<section className={styles.carryForwardBlock}>
			<h1 className={styles.title}>
				Objectives tab{" "}
				<Popup
					inverted
					size="tiny"
					content="This tab will be visible to all members in this stream"
					position="top center"
					trigger={<FontAwesomeIcon icon={faInfoCircle} />}
					className={styles.popup}
				/>
			</h1>

			<div className={cx(styles.rowContainer, styles.withSpaceBetween)}>
				<label htmlFor={toggleID} className={styles.label}>
					Show an Objectives tab in this Stream…
				</label>
				<CheckboxUI
					toggle
					checked={value}
					onChange={changeToggler}
					id={toggleID}
					className={cx(styles.toggle, stylessettings.settingsPageToggle)}
				/>
			</div>
		</section>
	);
}

interface ObjectivesPageProps {
	data: NonNullable<StreamSettingsResponseStream> | TeamSettingsResponseTeam;
	setSetting: (sourceId: string, setting: string) => (value: string | null) => () => void;
}

export function ObjectivesPage({ data, setSetting }: ObjectivesPageProps) {
	const [assignees, setAssignees] = useState<AssigneeType[]>([]);

	const checkIcon = <FontAwesomeIcon icon={faCheck} fixedWidth className={styles.checkIcon} />;
	const statusSetter = setSetting(data?.id ? data.id : "", "objectives_filter_status");
	const assigneeSetter = setSetting(data?.id ? data.id : "", "objectives_filter_assignee");
	const cycleSetter = setSetting(data?.id ? data.id : "", "objectives_filter_cycle");

	const handleAssigneesChange = useCallback(
		(assigneesList: AssigneeType[]) => {
			if (!data) {
				return;
			}
			setAssignees(assigneesList);

			const newValue = assigneesList?.map(a => a.team?.id || a.user?.id).filter((item): item is string => !!item) || [];
			if (!isEqual(newValue, data.objectivesFilterAssignee)) {
				assigneeSetter(JSON.stringify(newValue))();
			}
		},
		[setAssignees, assigneeSetter, data],
	);

	const handleCycleChange = useCallback(
		(newCycles: string[]) => {
			cycleSetter(JSON.stringify(newCycles))();
		},
		[cycleSetter],
	);

	if (!data || !data.id) {
		return null;
	}

	return (
		<main>
			{data.__typename === "NoteStream" && (
				<ObjectivesTabToggler
					value={!!data.objectivesTabEnabled}
					onChange={setSetting(data.id, "objectives_tab_enabled")}
				/>
			)}

			<section className={styles.carryForwardBlock}>
				<h1 className={styles.title}>
					Default filters{" "}
					<Popup
						inverted
						size="tiny"
						content="Sets the default filter for all users when visiting this tab"
						position="top center"
						trigger={<FontAwesomeIcon icon={faInfoCircle} />}
						className={styles.popup}
					/>
				</h1>

				<div className={cx(styles.rowContainer, styles.withTopMargin, styles.withSpaceBetween)}>
					<p className={styles.label}>Assignee:</p>
					{data.__typename !== "Team" ? (
						<AssigneeFilter
							initialAssigneeIds={data.objectivesFilterAssignee}
							values={assignees}
							setValues={handleAssigneesChange}
							className={styles.filterDropdown}
							menuPortalTarget={document.body}
						/>
					) : (
						<Popup
							size="tiny"
							content="You can only filter objectives for your team."
							position="top center"
							trigger={
								<div>
									<AssigneeFilter
										isDisabled
										initialAssigneeIds={data.objectivesFilterAssignee}
										values={assignees}
										setValues={handleAssigneesChange}
										className={styles.filterDropdown}
									/>
								</div>
							}
						/>
					)}
				</div>

				<div className={cx(styles.rowContainer, styles.withTopMargin, styles.withSpaceBetween)}>
					<p className={styles.label}>Status:</p>
					<ArrowDropdown
						className={styles.filterDropdown}
						heading="Select default status"
						text={
							data.objectivesFilterStatus ? StatusNames[data.objectivesFilterStatus as ObjectiveStatus] : "All statuses"
						}
						forceCloseOnOutsideClick
					>
						<DropdownItem key="status-none" onClick={statusSetter(null)}>
							All statuses
							{data.objectivesFilterStatus === null && checkIcon}
						</DropdownItem>
						{Object.keys(StatusNames).map(status => (
							<DropdownItem key={`status-${status}`} onClick={statusSetter(status as ObjectiveStatus)}>
								{StatusNames[status as ObjectiveStatus]}
								{data.objectivesFilterStatus === status && checkIcon}
							</DropdownItem>
						))}
					</ArrowDropdown>
				</div>

				<div className={cx(styles.rowContainer, styles.withTopMargin, styles.withSpaceBetween)}>
					<p className={styles.label}>Cycle:</p>

					<React.Suspense fallback={<Loader active inline />}>
						<CycleListInput
							isMulti
							selected={data.objectivesFilterCycle}
							onChange={handleCycleChange}
							className={styles.filterDropdown}
							fixViewportOverflow
						/>
					</React.Suspense>
				</div>
			</section>
		</main>
	);
}
